import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";

// What every modal dialog does with the keyboard (byj-1wv.51.6), so each is
// not its own copy: Esc is its alone — heard before anything else on the
// page (the capture phase) and taken (defaultPrevented), as Confirm does, so
// a popover or a picking left open under it stays — and, aria-modal, Tab
// goes round it and never out to the page under it, as Drawer does.

/** What Tab may land on. */
const focusable =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Tab and Shift+Tab kept inside `e.currentTarget`, from its last to its first and back. */
function trapTab(e: ReactKeyboardEvent<HTMLElement>) {
  if (e.key !== "Tab") return;
  const all = [...e.currentTarget.querySelectorAll<HTMLElement>(focusable)];
  const edge = e.shiftKey ? all[0] : all[all.length - 1];
  if (edge && document.activeElement === edge) {
    e.preventDefault();
    (e.shiftKey ? all[all.length - 1] : all[0])!.focus();
  }
}

/**
 * The dialogs open now, the last opened on top: capture listeners run in
 * the order they were added, the bottom dialog's first, so each asks
 * whether it is the top one before taking an Esc.
 */
const stack: object[] = [];

/**
 * Esc closes the dialog, and nothing under it; returns the dialog's
 * onKeyDown, which keeps Tab inside. `open` false, for a dialog kept mounted
 * while it is shut (the Drawer), it is not on the stack and hears nothing.
 */
export function useModalKeys(onClose: () => void, open = true): (e: ReactKeyboardEvent<HTMLElement>) => void {
  // the latest onClose, so a caller's new function each render does not re-listen
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const me = {};
    stack.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || stack[stack.length - 1] !== me) return;
      e.preventDefault();
      close.current();
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      stack.splice(stack.indexOf(me), 1);
    };
  }, [open]);
  return trapTab;
}
